import { IHrRegisterForm, IResetPasswordForm } from '@/types/auth';
import { apiClient } from '@/lib/api';
import { hrApiClient } from '@/lib/api';
import { IItemResponse } from '@/shared/base/base.dto';
import {
  ICheckExistsResponse,
  IRegister,
  ISignIn,
  ISignInFB,
  ISignInGoogle,
  ISignInResponse,
  IUser,
} from '@/shared';

/** POST /auth/register-company: амжилттай бол `{ data: { user, company }, message }` (алдаа бол HTTP 4xx). */
interface RegisterCompanyResult {
  user?: Partial<IUser>;
  company?: { id?: number };
}

export function register(data: IRegister) {
  return hrApiClient.post<IItemResponse<Partial<IUser>>>('/auth/signup', data);
}

function parseCheckExistsBody(data: unknown): ICheckExistsResponse {
  if (data && typeof data === 'object' && 'exists' in data) {
    return data as ICheckExistsResponse;
  }
  if (
    data &&
    typeof data === 'object' &&
    'data' in data &&
    data.data &&
    typeof data.data === 'object' &&
    'exists' in data.data
  ) {
    return data.data as ICheckExistsResponse;
  }
  return { exists: false, message: '', user: null };
}

export type OtpPurpose = 'register' | 'forget' | 'jolooch';

export async function checkPhone(phone: string, purpose: OtpPurpose = 'register') {
  const response = await hrApiClient.post<IItemResponse<{ sent?: boolean }>>(
    '/auth/send-otp',
    {
      phone,
      purpose,
    },
  );

  const body = response.data;
  const inner =
    body && typeof body === 'object' && 'data' in body ? body.data : undefined;
  const sent = inner?.sent === true;

  return {
    ...response,
    data: {
      success: sent,
      message:
        (body && typeof body === 'object' && 'message' in body
          ? String(body.message)
          : '') || '',
    },
  };
}

export async function verifyPhone(
  username: string,
  code: string,
  purpose: OtpPurpose = 'register',
) {
  const value = username.trim();
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  try {
    const response = await hrApiClient.post('/auth/verify-otp', {
      ...(isEmail ? { email: value } : { phone: value }),
      purpose,
      code,
    });

    return {
      ...response,
      data: {
        ...response.data,
        success: Boolean(response?.data?.data?.verified),
        message: response?.data?.message || '',
      },
    };
  } catch (error: unknown) {
    const axiosError = error as {
      response?: { data?: Record<string, unknown>; message?: string };
    };
    const errorData = axiosError?.response?.data || {};
    return {
      ...axiosError?.response,
      data: {
        ...errorData,
        success: false,
        message:
          (errorData?.message as string) ||
          'Кодын хугацаа дууссан эсвэл код илгээгдээгүй байна',
      },
    };
  }
}

export function emongoliaRegisterVerify(token: string, otpCode: string) {
  return apiClient.post('register/emongolia-verify', { token, otpCode });
}

export function emongoliaResendOtp(token: string) {
  return apiClient.post('/register/emongolia-resend-otp', { token });
}

export function resetPasswordCode(username: string) {
  const value = username.trim();
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  return hrApiClient.post('/auth/send-otp', {
    purpose: 'forget',
    ...(isEmail ? { email: value } : { phone: value }),
  });
}

export function resetPassword(data: IResetPasswordForm) {
  const value = data.username.trim();
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  return hrApiClient.post('/auth/reset-password', {
    ...(isEmail ? { email: value } : { phone: value }),
    code: data.code,
    password: data.password,
  });
}

export function login(payload: ISignIn) {
  return hrApiClient.post<ISignInResponse>('/auth/signin', payload);
}

export function loginByFacebook(payload: ISignInFB) {
  return hrApiClient.post<ISignInResponse>('/auth/signin-fb', payload);
}

export function loginByGoogle(payload: ISignInGoogle) {
  return hrApiClient.post<ISignInResponse>('/auth/signin-google', payload);
}

export async function checkPhoneUnique(username: string) {
  const { data } = await hrApiClient.get<ICheckExistsResponse>(
    '/auth/check-user-exists',
    { params: { username } },
  );

  const parsed = parseCheckExistsBody(data);

  return {
    data: {
      checkUserExists: {
        exists: Boolean(parsed.exists),
        message: parsed.message ?? '',
        user: parsed.user ?? null,
      },
    },
  };
}

export async function checkCompanyUnique(username: string) {
  const { data } = await hrApiClient.get<ICheckExistsResponse>(
    '/auth/check-company-exists',
    { params: { input: username } }
  );

  return {
    data: {
      checkCompanyExists: {
        exists: Boolean(data.exists),
        message: data.message ?? '',
      },
    },
  };
}

export async function createCompany(data: IHrRegisterForm) {
  const input = {
    company: {
      branch_id: Number(data.category_id),
      name: data.company_name,
      register: data.company_register,
      email: data.company_email,
    },
    user: {
      email: data.email,
      firstname: data.firstname,
      lastname: data.lastname,
      password: data.password,
      phone: Number(data.phone),
    },
  };

  const { data: response } = await hrApiClient.post<
    IItemResponse<RegisterCompanyResult>
  >('/auth/register-company', input);

  // Backend `status` талбар буцаадаггүй: user + company үүссэн бол амжилттай.
  const result = response.data;

  return {
    data: {
      registerCompany: {
        status: Boolean(result?.user?.id && result?.company?.id),
        message: response.message ?? '',
      },
    },
  };
}
